import { CookieStorage, getCookieDomain } from '@crossnokaye/ux-common';

export interface LastVisitedAgent {
  /** Organization slug used in URLs (e.g., 'lineage') */
  orgName: string;
  /** Human-readable organization name (e.g., 'Lineage Logistics') */
  orgDisplayName?: string;
  /** Facility shortname/slug (e.g., 'riverside') */
  shortname: string;
}

// Cookie key must match Darkwing for cross-app state sharing
const COOKIE_KEY = 'ck.lastVisitedAgent';
const THIRTY_DAYS_IN_SECONDS = 60 * 60 * 24 * 30;

const createCookieStorage = (): CookieStorage => {
  const domain = getCookieDomain(window.location.hostname);
  return new CookieStorage({
    domain,
    maxAge: THIRTY_DAYS_IN_SECONDS,
    sameSite: 'lax',
    path: '/',
    secure: window.location.protocol === 'https:',
  });
};

/**
 * Type guard to validate LastVisitedAgent objects at runtime.
 */
const isLastVisitedAgent = (value: unknown): value is LastVisitedAgent => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'orgName' in value &&
    'shortname' in value &&
    typeof value.orgName === 'string' &&
    value.orgName.length > 0 &&
    typeof value.shortname === 'string' &&
    value.shortname.length > 0 &&
    ('orgDisplayName' in value
      ? value.orgDisplayName === undefined || typeof value.orgDisplayName === 'string'
      : true)
  );
};

const parseLastVisitedAgent = (rawValue: string | null): LastVisitedAgent | undefined => {
  if (!rawValue) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(rawValue);
    if (isLastVisitedAgent(parsed)) {
      return parsed;
    }
  } catch {
    // Ignore parse errors and return undefined below
  }

  return undefined;
};

const readCookieValue = (storage: CookieStorage): LastVisitedAgent | undefined => {
  return parseLastVisitedAgent(storage.getItem(COOKIE_KEY));
};

const writeCookieValue = (storage: CookieStorage, value: LastVisitedAgent): void => {
  storage.setItem(COOKIE_KEY, JSON.stringify(value));
};

/**
 * Gets the currently cached agent from cross-domain cookie.
 * Returns undefined if no cached value exists.
 */
export function getCachedAgentSlug(): LastVisitedAgent | undefined {
  const storage = createCookieStorage();
  return readCookieValue(storage);
}

/**
 * Sets the cached agent in a cross-domain cookie.
 * Uses the same cookie key as Darkwing for cross-app state sharing.
 */
export function setCachedAgentSlugCookie(
  orgName: string,
  agentSlug: string,
  orgDisplayName?: string
): void {
  const storage = createCookieStorage();
  const value: LastVisitedAgent = {
    orgName,
    orgDisplayName,
    shortname: agentSlug,
  };
  writeCookieValue(storage, value);
}

/**
 * Updates the agent cache.
 * This is the primary function for updating the cached agent from router context.
 */
export const updateAgentCache = ({
  org,
  agent,
  orgDisplayName,
}: {
  org: string;
  agent: string;
  orgDisplayName?: string;
}) => {
  setCachedAgentSlugCookie(org, agent, orgDisplayName);
};
